/*
 * Based on https://github.com/skyslope/files-ui/blob/dev/src/hooks/use-post-message.ts
 */
import { environmentConfig } from '../lib/environment-config';

export enum MessagesToPrime {
  // Sent when the envelope has been sent and the user clicks the "I'm done" button
  DIGISIGN_ENVELOPE_SENT = 'digisign-envelope-sent',
}

export type PrimeMessageMetadata = {
  digisignEnvelopeId?: string;
};

export type PrimeMessagePayload = {
  status: MessagesToPrime;
  metadata?: PrimeMessageMetadata;
};

/**
 * Post a message to Prime when sender-ui is running in an iframe.
 * This follows the same pattern as files-ui's postMessageToPrime.
 *
 * Prime will receive a structured message with status and optional metadata.
 */
export function postMessageToPrime(status: MessagesToPrime, metadata?: PrimeMessageMetadata) {
  const payload: PrimeMessagePayload = {
    status,
    ...(metadata && { metadata }),
  };

  window.parent.postMessage(payload, '*');

  if (environmentConfig.envName !== 'prod') {
    console.info('postMessageToPrime: ', payload);
  }
}

/**
 * Payload for mobile app header/navigation messages.
 * The mobile app is very particular about this format, so be careful when modifying.
 */
export type MobileAppHeaderPayload = {
  header_text: string;
  back_button_type: string;
  should_back_button_exit: boolean;
  button_text: string;
  metadata?: {
    digisignEnvelopeId?: string;
  };
};

export type MobileAppStatusPayload = {
  status: string;
  message?: string;
  metadata?: {
    digisignEnvelopeId?: string;
  };
};

/**
 * Post a message to the mobile app webkit handler.
 * This is used when sender is running inside the SkySlope mobile app.
 *
 * IMPORTANT: The mobile app relies on these messages to handle
 * navigation, please use caution when using/modifying.
 */
export function postMessageToMobileApp(payload: MobileAppHeaderPayload | MobileAppStatusPayload) {
  // @ts-expect-error -- webkit is added to window by the mobile app at runtime
  if (window.webkit?.messageHandlers?.mobileAppListener) {
    // @ts-expect-error -- webkit is added to window by the mobile app at runtime
    window.webkit.messageHandlers.mobileAppListener.postMessage(JSON.stringify(payload));

    if (environmentConfig.envName !== 'prod') {
      console.info('postMessageToMobileApp: ', payload);
    }
  }
}
